import { useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth, API } from "@/App";
import { useSubscription } from "@/context/SubscriptionContext";
import { hasAccess, MEMBERSHIP_URL } from "@/lib/subscriptionAccess";
import { LockedOverlay } from "@/components/LockedOverlay";
import { BottomNavigation } from "@/components/layout/BottomNavigation";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { CommunityFeed, CreatePostDialog } from "@/components/CommunityFeed";
import { ShareButton } from "@/components/ShareFeatures";
import { BADGES, Badge } from "@/components/Badges";
import { AccountabilityPartners } from "@/components/AccountabilityPartners";
import { Leaderboard } from "@/components/Leaderboard";
import axios from "axios";
import { 
  Users, 
  Trophy, 
  Flame,
  ArrowLeft,
  Sparkles,
  Heart,
  TrendingUp,
  UserPlus,
  Crown,
  Mail,
  ShoppingBag,
  MessageCircle,
  ExternalLink,
  Bell
} from "lucide-react";

const TRUENORTH_STORE_URL = "https://www.truenorth-zone.com/";
const WHATSAPP_COMMUNITY_URL = "https://chat.whatsapp.com/I5ttgB4cPHQ9ajxVW5mInB";

export default function CommunityPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { token, user } = useAuth();
  const { subscription } = useSubscription();
  const [gamification, setGamification] = useState(null);
  
  // Read initial tab from URL query param (e.g., /community?tab=badges)
  const initialTab = searchParams.get('tab') || 'feed';
  const [activeTab, setActiveTab] = useState(initialTab);

  // Check if user has access to community (monthly+) - pass user for admin bypass
  const canAccessCommunity = hasAccess(subscription.type, 'monthly', user);

  const fetchGamificationData = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/gamification/stats`, {
        withCredentials: true
      });
      setGamification(res.data);
    } catch (err) {
      console.error("Failed to fetch gamification data:", err);
    }
  }, []);

  useEffect(() => {
    if (canAccessCommunity && token) {
      fetchGamificationData();
    }
  }, [canAccessCommunity, token, fetchGamificationData]);

  const earnedBadges = gamification?.badges?.filter(b => b.earned) || [];

  // Memoize data objects for ShareButton to prevent unnecessary re-renders
  const streakShareData = useMemo(() => ({ 
    days: gamification?.streak?.current_streak || 0 
  }), [gamification?.streak?.current_streak]);

  // Show locked page for free users
  if (!canAccessCommunity) {
    return (
      <div className="min-h-screen bg-background pb-20" data-testid="community-page-locked">
        {/* Header */}
        <header 
          className="sticky top-0 z-40 backdrop-blur-lg border-b border-black/5"
          style={{
            backgroundImage: `linear-gradient(rgba(11,40,33,0.85), rgba(11,40,33,0.9)), url('https://customer-assets.emergentagent.com/job_path-unfolds/artifacts/va4i7lvj_ChatGPT%20Image%20Feb%2014%2C%202026%2C%2012_49_01%20PM.png')`,
            backgroundSize: 'cover',
            backgroundPosition: 'center'
          }}
        >
          <div className="px-4 py-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Button 
                variant="ghost" 
                size="sm" 
                className="h-8 w-8 p-0"
                onClick={() => navigate("/dashboard")}
              >
                <ArrowLeft className="w-5 h-5 text-[#a69373]" />
              </Button>
              <div>
                <h1 className="text-lg font-heading text-[#a69373]">Community</h1>
                <p className="text-xs text-[#a69373]/60 font-semibold">Share your journey</p>
              </div>
            </div>
          </div>
        </header>

        {/* Locked Content */}
        <div className="flex items-center justify-center min-h-[60vh] px-4">
          <Card className="glass-card max-w-md w-full">
            <CardContent className="p-8 text-center">
              <div className="w-20 h-20 rounded-full bg-[#a69373]/20 flex items-center justify-center mx-auto mb-6">
                <Users className="w-10 h-10 text-[#a69373]" />
              </div>
              <h2 className="text-xl font-heading text-white mb-3">Upgrade to join the community</h2>
              <p className="text-white/60 text-sm mb-6">
                Connect with fellow travelers, share achievements, and find accountability partners. 
                Community access is available on all paid plans.
              </p>
              <a
                href={MEMBERSHIP_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-6 py-3 rounded-xl bg-[#a69373] hover:bg-[#b89a5e] text-[#0B2821] font-semibold transition-colors"
                data-testid="community-upgrade-btn"
              >
                <Crown className="w-5 h-5" />
                Upgrade Now
                <ExternalLink className="w-4 h-4" />
              </a>
            </CardContent>
          </Card>
        </div>

        <BottomNavigation />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background pb-20" data-testid="community-page">
      {/* Header */}
      <header 
        className="sticky top-0 z-40 backdrop-blur-lg border-b border-black/5"
        style={{
          backgroundImage: `linear-gradient(rgba(11,40,33,0.85), rgba(11,40,33,0.9)), url('https://customer-assets.emergentagent.com/job_path-unfolds/artifacts/va4i7lvj_ChatGPT%20Image%20Feb%2014%2C%202026%2C%2012_49_01%20PM.png')`,
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        }}
      >
        <div className="px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button 
              variant="ghost" 
              size="sm" 
              className="h-8 w-8 p-0"
              onClick={() => navigate(-1)}
            >
              <ArrowLeft className="w-5 h-5 text-[#a69373]" />
            </Button>
            <div>
              <h1 className="text-lg font-heading text-[#a69373]">Community</h1>
              <p className="text-xs text-[#a69373]/60 font-semibold">Share your journey</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button className="w-9 h-9 rounded-full bg-black/30 backdrop-blur-sm flex items-center justify-center">
              <Bell className="w-4 h-4 text-[#a69373]" />
            </button>
            <a 
              href="https://www.truenorth-zone.com/"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-black/30 backdrop-blur-sm text-[#a69373] text-sm font-semibold hover:bg-black/40 transition-colors"
              style={{ zIndex: 9999, position: 'relative', pointerEvents: 'all' }}
            >
              <ShoppingBag className="w-4 h-4" />
              Shop
            </a>
          </div>
        </div>
      </header>

      <main className="px-4 py-6 space-y-6">
        {/* WhatsApp Live Support Group - TOP */}
        <Card className="glass-card overflow-hidden border-[#25D366]/30" data-testid="whatsapp-community">
          <CardContent className="p-4">
            <div className="flex flex-col gap-4">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-xl bg-[#25D366]/20 flex items-center justify-center shrink-0">
                  <MessageCircle className="w-6 h-6 text-[#25D366]" />
                </div>
                <div>
                  <h2 className="text-base font-heading text-[#a69373]">Live Support Group</h2>
                  <p className="text-white/60 text-xs">
                    Real conversations with real people. Join our WhatsApp group for daily support and accountability.
                  </p>
                </div>
              </div>
              <Button
                className="w-full bg-[#25D366] hover:bg-[#20BD5A] text-sm py-3"
                onClick={() => window.open(WHATSAPP_COMMUNITY_URL, '_blank', 'noopener,noreferrer')}
                data-testid="whatsapp-join-btn"
                style={{ color: '#ffffff' }}
              >
                <MessageCircle className="w-4 h-4 mr-2 shrink-0" style={{ color: '#ffffff' }} />
                <span style={{ color: '#ffffff' }}>Join the WhatsApp Group</span>
                <ExternalLink className="w-3 h-3 ml-2 shrink-0" style={{ color: '#ffffff' }} />
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* In-App Community Card */}
        <Card className="glass-card relative overflow-hidden" data-testid="community-optin">
          <div className="absolute top-3 right-3 px-2 py-0.5 rounded bg-white/10 text-white/50 text-xs">Coming Soon</div>
          <CardContent className="p-5 text-center">
            <p className="text-[#a69373]/60 text-xs font-semibold uppercase tracking-wider mb-2">IN-APP</p>
            <div className="w-14 h-14 rounded-full bg-[#a69373]/20 flex items-center justify-center mx-auto mb-3">
              <Users className="w-7 h-7 text-[#a69373]" />
            </div>
            <h2 className="text-lg font-heading text-[#a69373] mb-2">Join the Community</h2>
            <p className="text-white/70 text-sm mb-4 max-w-sm mx-auto">
              Connect with others on the same journey. Share progress, find accountability, and go deeper together.
            </p>
            <Button 
              className="btn-primary mb-2"
              onClick={() => {
                const subject = encodeURIComponent("Community Opt-In");
                const body = encodeURIComponent("Hi, I'd like to join the Compass community when it launches.");
                window.location.href = `mailto:info@truenorth-zone.com?subject=${subject}&body=${body}`;
              }}
              data-testid="community-optin-btn"
            >
              <Mail className="w-4 h-4 mr-2" />
              Count Me In
            </Button>
            <p className="text-white/40 text-xs">We'll notify you when the community opens.</p>
          </CardContent>
        </Card>
        {/* Your Stats Summary */}
        {gamification && (
          <Card className="glass-card overflow-hidden" data-testid="your-stats">
            <CardContent className="p-4">
              <div className="flex items-center justify-between mb-4">
                <h2 className="font-heading font-bold" style={{ color: '#a69373' }}>Your Journey</h2>
                <ShareButton 
                  type="streak" 
                  data={streakShareData}
                  variant="outline"
                  size="sm"
                />
              </div>
              
              <div className="grid grid-cols-3 gap-4">
                <div className="text-center">
                  <div className="w-10 h-10 rounded-full bg-orange-500/10 flex items-center justify-center mx-auto mb-2">
                    <Flame className="w-5 h-5 text-orange-500" />
                  </div>
                  <p className="text-xl font-heading text-[#a69373]">{gamification.streak.current_streak}</p>
                  <p className="text-xs text-[#a69373]/50">Day Streak</p>
                </div>
                
                <div className="text-center">
                  <div className="w-10 h-10 rounded-full bg-purple-500/10 flex items-center justify-center mx-auto mb-2">
                    <Trophy className="w-5 h-5 text-purple-500" />
                  </div>
                  <p className="text-xl font-heading text-[#a69373]">{gamification.stats.badges_earned}</p>
                  <p className="text-xs text-[#a69373]/50">Badges</p>
                </div>
                
                <div className="text-center">
                  <div className="w-10 h-10 rounded-full bg-blue-500/10 flex items-center justify-center mx-auto mb-2">
                    <TrendingUp className="w-5 h-5 text-blue-500" />
                  </div>
                  <p className="text-xl font-heading text-[#a69373]">{gamification.streak.total_checkins}</p>
                  <p className="text-xs text-[#a69373]/50">Check-ins</p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="grid w-full grid-cols-4 bg-[#e0ebe0]">
            <TabsTrigger 
              value="feed" 
              className="data-[state=active]:bg-white data-[state=active]:text-[#a69373] text-xs sm:text-sm"
            >
              <Sparkles className="w-4 h-4 mr-1 hidden sm:inline" />
              Feed
            </TabsTrigger>
            <TabsTrigger 
              value="leaderboard"
              className="data-[state=active]:bg-white data-[state=active]:text-[#a69373] text-xs sm:text-sm"
            >
              <Crown className="w-4 h-4 mr-1 hidden sm:inline" />
              Leaders
            </TabsTrigger>
            <TabsTrigger 
              value="partners"
              className="data-[state=active]:bg-white data-[state=active]:text-[#a69373] text-xs sm:text-sm"
            >
              <UserPlus className="w-4 h-4 mr-1 hidden sm:inline" />
              Partners
            </TabsTrigger>
            <TabsTrigger 
              value="achievements"
              className="data-[state=active]:bg-white data-[state=active]:text-[#a69373] text-xs sm:text-sm"
            >
              <Trophy className="w-4 h-4 mr-1 hidden sm:inline" />
              Badges
            </TabsTrigger>
          </TabsList>
          
          <TabsContent value="feed" className="mt-4">
            <CommunityFeed />
          </TabsContent>
          
          <TabsContent value="leaderboard" className="mt-4">
            <Leaderboard />
          </TabsContent>
          
          <TabsContent value="partners" className="mt-4">
            <AccountabilityPartners />
          </TabsContent>
          
          <TabsContent value="achievements" className="mt-4 space-y-4">
            {/* Earned Badges */}
            <Card className="glass-card">
              <CardContent className="p-4">
                <h3 className="font-heading text-[#a69373] mb-4">Your Badges</h3>
                
                {earnedBadges.length === 0 ? (
                  <div className="text-center py-6">
                    <Trophy className="w-12 h-12 text-[#d4e4d4] mx-auto mb-3" />
                    <p className="text-[#a69373]/50 text-sm">
                      Complete check-ins to earn your first badge!
                    </p>
                  </div>
                ) : (
                  <div className="grid grid-cols-4 gap-4">
                    {earnedBadges.map(badge => {
                      const badgeDef = BADGES[badge.badge_id];
                      if (!badgeDef) return null;
                      return (
                        <div key={badge.badge_id} className="relative">
                          <Badge badge={badgeDef} earned={true} size="md" />
                          <ShareButton 
                            type="badge" 
                            data={{ name: badgeDef.name, description: badgeDef.description }}
                            variant="ghost"
                            size="sm"
                            className="absolute -top-1 -right-1 h-6 w-6 p-0 bg-white rounded-full shadow-sm"
                          />
                        </div>
                      );
                    })}
                  </div>
                )}
              </CardContent>
            </Card>
            
            {/* All Badges */}
            <Card className="glass-card">
              <CardContent className="p-4">
                <h3 className="font-heading text-[#a69373] mb-4">All Badges</h3>
                <p className="text-[#a69373]/50 text-sm mb-4">
                  {gamification?.stats.badges_earned || 0} of {gamification?.stats.badges_total || 13} earned
                </p>
                
                <div className="grid grid-cols-4 sm:grid-cols-5 gap-4">
                  {Object.entries(BADGES).map(([id, badgeDef]) => {
                    const earned = earnedBadges.some(b => b.badge_id === id);
                    return (
                      <Badge 
                        key={id} 
                        badge={badgeDef} 
                        earned={earned} 
                        size="sm" 
                      />
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </main>

      <BottomNavigation />
    </div>
  );
}
